/**
 * Who sees which steps.
 *
 * Pure functions, no React, no DOM — the eligibility rules are the part most likely to be argued
 * about later, so they are testable without mounting anything.
 *
 * The rule the task fixes and this file implements: a step the user cannot perform is REMOVED, not
 * greyed out. Greying it out teaches a new hire that the app has a button they cannot find, and on a
 * click-to-advance tour a disabled step is also a dead end — there is nothing to click. A foreman
 * walking the daily-report tour simply never hears about Manager Approve.
 */
import { getAccessibleNavModules } from '../constants/modules'

/**
 * @param {object} args
 * @param {object|null} args.user           the useAuth() user
 * @param {object} args.features            tenant feature flags from useTenantBranding()
 * @param {(perm: string) => boolean} args.hasPermission  useAuth().hasPermission
 * @param {object} [args.switches]          the server's module switches from useModuleSwitches()
 */
export function buildTourContext({ user, features, hasPermission, switches }) {
  // Deliberately NOT a second copy of the role→module map. getAccessibleNavModules already folds in
  // the role defaults, the per-user `accessible_modules` override the backend sends at login, the
  // FEATURE_GATED table (empty today, the mechanism for any future entitlement-gated module) and the
  // HELD_MODULES list with the server's switches (Capital, Capex and Invoicing until released).
  // Re-implementing the predicate here is how the tour would end up pointing at a module's nav tab
  // that this tenant does not have.
  const moduleIds = new Set(getAccessibleNavModules(user, features || {}, switches || {}).map((m) => m.id))
  return {
    user: user || null,
    moduleIds,
    // Lowercased once here. Several screens read bfs_user straight out of localStorage and compare
    // the role themselves (WeeklyTimeView.jsx:25, NavBar.jsx:21), and they all lowercase at the
    // point of use; matching that exactly is the whole job of the `role` gate below.
    role: String((user && user.role) || '').toLowerCase(),
    hasPermission: typeof hasPermission === 'function' ? hasPermission : () => false,
    // The server's module switches, for a tour that needs one beyond its own nav module
    // (`requiresSwitch`). Only a literal `true` is on — the same rule isModuleReleased reads them by.
    switches: switches || {},
  }
}

/**
 * Does this person hold the grant — or, for a list, EVERY grant in it? See tourSchema's
 * validatePermission for why a list means "all of" and never "any of".
 */
export function holdsPermission(ctx, permission) {
  if (permission == null) return true
  return [].concat(permission).every((p) => ctx.hasPermission(p))
}

/** Can this user reach and perform this one step? */
export function isStepVisible(step, ctx) {
  if (!ctx || !ctx.user) return false
  if (step.module && !ctx.moduleIds.has(step.module)) return false
  // Permission, not role, WHENEVER PERMISSION CAN SAY IT. useAuth.hasPermission is the tier the
  // backend actually enforces.
  if (step.permission && !holdsPermission(ctx, step.permission)) return false
  // …and `role` for the cases where permission provably cannot. Two facts force this exception:
  // hasPermission returns true for EVERY permission when the role is admin/master_admin
  // (useAuth.jsx:346-351), so no permission string can exclude an admin; and some controls are
  // rendered off a literal role comparison rather than a grant — the Crew Hours tab is
  // `role === 'foreman'` (WeeklyTimeView.jsx:25), which an admin does not satisfy.
  //
  // Note this is an EXACT match and deliberately NOT useAuth.hasRole, which short-circuits true for
  // any admin (useAuth.jsx:328-338). Using hasRole here would reintroduce the exact bug the gate
  // exists to prevent: an admin offered a step pointing at a tab they cannot see.
  if (step.role && ctx.role !== step.role) return false
  return true
}

export function visibleSteps(tour, ctx) {
  if (!tour || !Array.isArray(tour.steps)) return []
  return tour.steps.filter((s) => isStepVisible(s, ctx))
}

/**
 * Tours this user should be offered.
 *
 * A tour whose every step is filtered out does not appear at all. Listing it and then opening an
 * empty overlay is the worst of both — the user learns the help is broken, which costs more trust
 * than never advertising the tour.
 *
 * A tour also disappears when its declared `module` is not one the user has, even if some step
 * survived: a "Fleet basics" tour has no business in the menu of someone with no Fleet. Same for a
 * declared `role` — the crew-time tour opens on a nav tab EVERY role has, so per-step filtering
 * alone would leave an admin holding a one-step stub of a tour written for a foreman.
 *
 * …and the same for a declared `permission`, which exists for the case per-step gating provably
 * cannot express: a tour whose OPENING steps are navigation (a nav tab, a New button — rendered for
 * everyone, so correctly ungated) and whose later steps all need one grant. Filter those steps out
 * and the ungated openers still survive, `steps.length > 0` still holds, and the launcher advertises
 * a 2-step stub that walks a new hire to a create screen the backend answers with a 403. A tour-level
 * permission says what is actually true: this tour stands or falls as a whole.
 */
export function availableTours(tours, ctx) {
  if (!ctx || !ctx.user) return []
  return tours
    .filter((t) => !t.module || ctx.moduleIds.has(t.module))
    .filter((t) => !t.role || ctx.role === t.role)
    .filter((t) => !t.permission || holdsPermission(ctx, t.permission))
    // A held server switch the tour's screens cannot work without (tourSchema `requiresSwitch`).
    .filter((t) => !t.requiresSwitch || (ctx.switches && ctx.switches[t.requiresSwitch] === true))
    .map((t) => ({ tour: t, steps: visibleSteps(t, ctx) }))
    .filter((entry) => entry.steps.length > 0)
}
